import { setThrottle, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { loadOlderEpgsForChannel, playVideo, setPlayerControlsState } from 'common/actions';
import { Progressbar } from 'common/components/progressbar';
import { HAS_ACCESS, TIME_BEFORE_BUFFER } from 'common/config/constants';
import variables from 'common/config/variables';
import { Epg, LiveChannels } from 'common/constants/data-types';
import { FocusableItem } from 'common/interfaces';
import { IVideoPlayer, PlayerControlsState } from 'common/reducers/videoPlayer';
import { calculateTimePercentages, getLiveTimestamp, getToday } from 'common/services/helpers';
import { getClassName, getRemoteKeyName } from 'common/utils';
import { useHoverable } from 'common/utils/hooks';
import { isArray, isEmpty, isNil } from 'lodash';
import useCurrentTime from 'Player/hooks/useCurrentTime';
import React, {
  MouseEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { connect } from 'react-redux';
import { PlayerControlsProps } from './Controls';
import FastSeekStripe from './FastSeekStripe';
import SeekbarPoint from './SeekbarPoint';
import StreamTimes from './StreamTimes';
const { FAST_SEEK_OFFSET } = variables;

interface SeekOptions {
  timestamp: number;
  epgIndex: number;
}

interface SeekbarProps {
  // Redux injected
  id?: string;
  allEpgs?: Epg[];
  epgs?: IVideoPlayer['epgs'];
  playerControlsState: PlayerControlsState;
  setPlayerControlsState: (playerControlsState: PlayerControlsState) => void;
  loadOlderEpgsForChannel: (channelId: string, untilTimestamp?: number) => void;
  playVideo: (currentepg: Epg, options?: any) => void;
  nextVideoEpg?: Epg;
  visible?: boolean;
  currentVideoStart?: number;
  currentVideoStop?: number;
  channelAccess?: number;
  vod?: boolean;
  fastZapping?: boolean;
  isPlayerLoading?: boolean;
  isLiveEpg?: number;
  //
  pause: PlayerControlsProps['pause'];
  play: PlayerControlsProps['play'];
  setCurrentTime: PlayerControlsProps['setCurrentTime'];
  getDuration: PlayerControlsProps['getDuration'];
  focusKey?: FocusableItem['focusKey'];
  disabled?: FocusableItem['disabled'];
  hidden?: boolean;
}
function Seekbar(props: SeekbarProps) {
  const { ref, focused } = useFocusable({
    focusKey: props.focusKey,
    focusable: !props.disabled && !props.hidden,
    isFocusBoundary: true,
    onArrowPress: (direction: string) => {
      if (['left', 'right'].includes(direction) && props.channelAccess === HAS_ACCESS) {
        handleSeeking(direction);
      }
      return props.playerControlsState === 'expanded';
    }
  });

  const playerControlsStateRef = useRef(props.playerControlsState);
  const seekbarVisible = useMemo(() => {
    playerControlsStateRef.current = props.playerControlsState;
    return !props.hidden && props.playerControlsState !== 'hidden';
  }, [props.hidden, props.playerControlsState]);
  const { active, onMouseEnter, onMouseLeave } = useHoverable(focused);
  const { currentTime: currentTimeHookValue, getCurrentTime } = useCurrentTime(seekbarVisible);

  const defCurrentTime = useDeferredValue(currentTimeHookValue);

  const [seekOptions, setSeekOptions] = useState<SeekOptions>({ epgIndex: -1, timestamp: -1 });
  const seekOptionsRef = useRef<SeekOptions>(seekOptions);
  useEffect(() => {
    seekOptionsRef.current = seekOptions;
  }, [seekOptions]);

  useEffect(() => {
    setThrottle({
      throttle: props.playerControlsState === 'seeking' ? 0 : variables.KEYPRESS_TIMEOUT,
      throttleKeypresses: true
    });
  }, [props.playerControlsState]);

  // older days prepend to allEpgs, keep the seek on the same show
  const prevAllEpgsRef = useRef(props.allEpgs);
  useEffect(() => {
    const prev = prevAllEpgsRef.current;
    prevAllEpgsRef.current = props.allEpgs;
    const { epgIndex } = seekOptionsRef.current;
    const seeked = prev && epgIndex >= 0 ? prev[epgIndex] : undefined;
    if (!seeked || !props.allEpgs || props.allEpgs[epgIndex] === seeked) {
      return;
    }
    const index = props.allEpgs.findIndex(
      (e) => e.start === seeked.start && e.chan_id === seeked.chan_id
    );
    if (index !== -1) {
      setSeekOptions((current) => ({ ...current, epgIndex: index }));
    }
  }, [props.allEpgs]);

  const currentEpgRef = useRef<Epg | undefined>(undefined);
  const currentEpg = useMemo((): Epg | undefined => {
    try {
      const current = props.epgs?.current;
      const calculatedEpg =
        props.allEpgs && seekOptions.epgIndex !== -1 && props.allEpgs[seekOptions.epgIndex];
      return (
        props.nextVideoEpg ||
        (props.id === (calculatedEpg as Epg)?.chan_id && calculatedEpg) ||
        current
      );
    } catch (error) {
      console.warn(error);
      return undefined;
    }
  }, [seekOptions, props.allEpgs, props.nextVideoEpg, props.epgs?.current]);

  useEffect(() => {
    currentEpgRef.current = currentEpg;
  }, [currentEpg]);

  const epgsRef = useRef(props.epgs);
  useEffect(() => {
    epgsRef.current = props.epgs;
  }, [props.epgs]);

  const epgsLoaded = useMemo(
    () => !isNil(props.epgs?.initialToIndex),
    [props.epgs?.initialToIndex]
  );

  const initialEpgKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const current = props.epgs?.current;
    const key = current ? `${current.chan_id}:${current.start}` : undefined;
    const samePlayingEpg = key === initialEpgKeyRef.current;
    initialEpgKeyRef.current = key;
    if (samePlayingEpg && seekOptionsRef.current.timestamp !== -1) {
      return;
    }
    if (epgsLoaded && seekOptionsRef.current.epgIndex !== props.epgs?.initialToIndex) {
      setSeekOptions((current) => ({
        ...current,
        epgIndex: props.epgs!.initialToIndex
      }));
    }
  }, [props.epgs?.initialToIndex, epgsLoaded]);

  useEffect(() => {
    // Reset state and play stream
    if (props.playerControlsState === 'hidden' && seekOptions.timestamp !== -1) {
      props.epgs && setSeekOptions({ epgIndex: props.epgs.initialToIndex, timestamp: -1 });
      props.play();
    }
  }, [props.playerControlsState]);

  const currentTime = useMemo(() => {
    try {
      if (props.vod) {
        return props.playerControlsState === 'seeking' ? seekOptions.timestamp : defCurrentTime;
      }
      if (isEmpty(currentEpg)) {
        throw new Error('No current EPG');
      }
      const currentRunningEpg = props.epgs?.current;
      const timestamp =
        seekOptions.timestamp !== -1 ? seekOptions.timestamp - currentEpg.start : -1;
      const hasNextVideo = !isEmpty(props.nextVideoEpg);
      const showRealCurrentTime =
        props.playerControlsState === 'channels' &&
        currentEpg.now &&
        props.nextVideoEpg?.chan_id === currentRunningEpg?.chan_id;
      if (hasNextVideo && !showRealCurrentTime) {
        // If there is a next video and we shouldn't show the real current time,
        // return the live timestamp offset by the current EPG's start time
        return getLiveTimestamp() - currentEpg.start;
      }

      if (timestamp !== -1) {
        // If seeking, return the seek timestamp
        return timestamp;
      }

      // Otherwise, return the deferred current time, offset if the EPG has changed
      const offset = currentRunningEpg?.start === currentEpg.start ? 0 : currentEpg.start;
      return defCurrentTime + offset;
    } catch (error) {
      return 0;
    }
  }, [
    props.nextVideoEpg,
    currentEpg,
    defCurrentTime,
    seekOptions,
    props.playerControlsState,
    props.epgs?.current,
    props.vod
  ]);

  const duration = useMemo(() => {
    try {
      if (props.vod) return props.getDuration();

      if (isEmpty(currentEpg)) {
        throw new Error('No current EPG');
      }
      return currentEpg.stop - currentEpg.start;
    } catch (error) {
      const { currentVideoStart, currentVideoStop } = props;
      // Fallback to current video start and stop
      return currentVideoStart && currentVideoStop ? currentVideoStop - currentVideoStart : 0;
    }
  }, [currentEpg, props.currentVideoStart, props.currentVideoStop, props.vod, props.getDuration]);

  const percentage = useMemo(() => {
    try {
      if (props.vod) {
        return calculateTimePercentages(0, duration || 0, currentTime);
      }
      if (!currentEpg) {
        return 0;
      }
      const { start, stop, now } = currentEpg;
      const livePosition = calculateTimePercentages(
        start,
        stop,
        getLiveTimestamp() - TIME_BEFORE_BUFFER
      );
      const currentPosition = calculateTimePercentages(start, stop, currentTime + start);
      return now ? [currentPosition, livePosition] : currentPosition;
    } catch (error) {
      console.warn(error);
      return 0;
    }
  }, [seekOptions, props.allEpgs, currentTime, currentEpg, props.nextVideoEpg, props.vod]);

  const firstEpgStart = useMemo(() => {
    return props.allEpgs?.[0]?.start || 0;
  }, [props.allEpgs]);

  useEffect(() => {
    if (props.playerControlsState === 'seeking' && props.visible) {
      props.pause();
    }
  }, [props.playerControlsState]);

  const handleSeeking = useCallback(
    (direction: 'left' | 'right' | string) => {
      // If no current EPG or fast zapping is enabled, do not seek
      if (
        !currentEpgRef.current ||
        props.fastZapping ||
        seekOptionsRef.current.epgIndex === undefined ||
        props.isPlayerLoading
      ) {
        return;
      }
      playerControlsStateRef.current !== 'seeking' && props.setPlayerControlsState('seeking');
      const { start, stop, now: isLiveEpg } = currentEpgRef.current;
      const hasEnd = props.vod || isLiveEpg;
      const clampToTimestamp = !hasEnd || !epgsLoaded;
      const offset = FAST_SEEK_OFFSET * (direction === 'left' ? -1 : 1);
      let newSeekTime = seekOptionsRef.current.timestamp + offset;
      if (seekOptionsRef.current.timestamp === -1) {
        newSeekTime += getCurrentTime() + start;
      }
      const now = Math.floor(getToday().getTime() / 1000);
      const end = props.vod ? duration : now - TIME_BEFORE_BUFFER;
      let { epgIndex } = seekOptionsRef.current;

      // Clamp to end if seeking right and at/over end
      if (direction === 'right' && hasEnd && newSeekTime > end) {
        newSeekTime = end;
      }
      // Clamp to start if seeking left and at/below zero
      else if (direction === 'left' && hasEnd && newSeekTime <= 0) {
        newSeekTime = 0;
      }
      // Move to previous EPG if before current is before starting of current epg and not VOD
      else if (!props.vod && newSeekTime < start && epgIndex > 0 && epgsLoaded) {
        epgIndex--;
      }
      // Load the previous day if at the oldest loaded EPG
      else if (!props.vod && newSeekTime < start && epgIndex === 0 && epgsLoaded && props.id) {
        props.loadOlderEpgsForChannel(props.id, start - 1);
        newSeekTime = start;
      }
      // Move to next EPG if after current is at the end and not VOD
      else if (
        !props.vod &&
        newSeekTime > stop &&
        epgIndex < (props.allEpgs?.length || 1) - 1 &&
        epgsLoaded
      ) {
        epgIndex++;
      }
      // Clamp to start if before we go before starting of current epg
      // Fallback if we are at the first EPG
      else if (newSeekTime <= start && clampToTimestamp) {
        newSeekTime = start;
      }
      // Clamp to stop if after we go after stop
      // Fallback if we are at the last EPG
      else if (newSeekTime >= stop && clampToTimestamp) {
        newSeekTime = stop;
      }
      setSeekOptions({ epgIndex, timestamp: newSeekTime });
    },
    [seekOptions, props.pause, props.vod, props.fastZapping, props.isPlayerLoading, epgsLoaded]
  );

  const handleSeekFinish = useCallback(() => {
    const onSeeked = () => setSeekOptions((current) => ({ ...current, timestamp: -1 }));

    if (
      playerControlsStateRef.current !== 'seeking' ||
      !currentEpgRef.current ||
      !epgsRef.current
    ) {
      return;
    }
    const { initialToIndex } = epgsRef.current;
    const currentTime = props.vod
      ? seekOptionsRef.current.timestamp
      : seekOptionsRef.current.timestamp - currentEpgRef.current.start;
    if (
      !isNil(seekOptionsRef.current.epgIndex) &&
      seekOptionsRef.current.epgIndex !== initialToIndex
    ) {
      props.playVideo(currentEpgRef.current, { startPosition: currentTime, isFullscreen: true });
      onSeeked();
    } else {
      !isNil(seekOptionsRef.current.timestamp) && props.setCurrentTime(currentTime, onSeeked);
    }
    props.play();
    props.setPlayerControlsState('mini');
  }, [props.play, props.vod]);

  const handleMouseSeek = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left; //x position within the element.
      const flags = e.buttons;
      const primaryMouseButtonDown = (flags & 1) === 1;
      if (primaryMouseButtonDown && duration) {
        const percentage = x / rect.width;
        props.setCurrentTime(duration * percentage);
      }
    },
    [duration]
  );

  const handleKeyPress = (e: KeyboardEvent) => {
    setTimeout(() => {
      const key = getRemoteKeyName(e.keyCode);
      const direction = key === 'REWIND' ? 'left' : key === 'FASTFORWARD' ? 'right' : null;
      direction && handleSeeking(direction);
      if (
        key &&
        ['PLAY', 'PAUSE', 'PLAY_PAUSE', 'OK'].includes(key) &&
        playerControlsStateRef.current === 'seeking'
      ) {
        handleSeekFinish();
        e.preventDefault();
        e.stopPropagation();
      }
    }, 0);
  };

  useEffect(() => {
    props.visible && document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [props.visible]);

  return (
    <div className={getClassName('seekbar-container', { visible: !props.hidden })} ref={ref}>
      <StreamTimes
        visible={props.playerControlsState !== 'channels'}
        currentTime={currentTime}
        duration={duration}
      />
      <div className="seekbar-inner-container">
        {currentEpg && (
          <FastSeekStripe
            visible={props.playerControlsState === 'seeking'}
            chanId={props.vod ? currentEpg.id : currentEpg.chan_id}
            timestamp={seekOptions.timestamp}
            vod={props.vod}
            firstEpgStart={firstEpgStart}
          />
        )}
        <div
          className="flex row"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onMouseMove={handleMouseSeek}
          onMouseDown={handleMouseSeek}
        >
          <Progressbar customRef={ref} active={active} value={percentage} background>
            <SeekbarPoint
              percentage={isArray(percentage) ? percentage[0] : percentage}
              active={active}
            />
          </Progressbar>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({
  videoPlayer,
  liveChannels
}: {
  videoPlayer: IVideoPlayer;
  liveChannels: LiveChannels;
}) => {
  return {
    id: videoPlayer.channelId,
    epgs: videoPlayer.epgs,
    currentVideoStart: videoPlayer.video.start,
    currentVideoStop: videoPlayer.video.stop,
    allEpgs: videoPlayer.allEpgs,
    nextVideoEpg:
      isEmpty(videoPlayer.nextVideo) || isEmpty(liveChannels)
        ? undefined
        : liveChannels[videoPlayer.nextVideo.channelId].currentepg,
    playerControlsState: videoPlayer.playerControlsState,
    visible: videoPlayer.isPlaying && videoPlayer.video?.isFullscreen,
    channelAccess: videoPlayer.video?.access,
    vod: videoPlayer.video?.vod,
    fastZapping: videoPlayer.video?.fastZapping,
    isPlayerLoading: !videoPlayer.isPlaying,
    isLiveEpg: videoPlayer.video?.isLiveEpg
  };
};

export default React.memo(
  connect(mapStateToProps, { setPlayerControlsState, playVideo, loadOlderEpgsForChannel })(Seekbar)
);
